import {
  DragDropContext,
  Draggable,
  Droppable,
  type DropResult,
} from "@hello-pangea/dnd";
import { ChevronsLeftRight } from "lucide-react";
import type { Identifier } from "ra-core";
import { useState, type ReactNode } from "react";

import { cn } from "@/lib/utils";

import type { Option } from "./gulaConstants";

type KanbanItem = { id: Identifier; stage: string };

interface KanbanBoardProps<T extends KanbanItem> {
  columns: Option[];
  items: T[];
  renderCard: (item: T) => ReactNode;
  onMove: (item: T, toStage: string) => void;
  columnFooter?: (stage: string, items: T[]) => ReactNode;
  /** Color of each column's top strip (Tailwind background class) */
  accents?: Record<string, string>;
  /** Columns that start folded as a narrow drop zone (still accept drops) */
  foldedStages?: string[];
}

/**
 * Generic drag & drop board: one column per stage, cards sorted as given.
 * Moving a card calls onMove; the caller decides what happens (direct update
 * or a dialog asking for more data).
 */
export function KanbanBoard<T extends KanbanItem>({
  columns,
  items,
  renderCard,
  onMove,
  columnFooter,
  accents = {},
  foldedStages = [],
}: KanbanBoardProps<T>) {
  const [folded, setFolded] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(foldedStages.map((stage) => [stage, true])),
  );
  const byStage = (stage: string) =>
    items.filter((item) => item.stage === stage);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) {
      return;
    }
    const item = items.find(
      (candidate) => String(candidate.id) === draggableId,
    );
    if (item) {
      onMove(item, destination.droppableId);
    }
  };

  const toggle = (stage: string) =>
    setFolded((previous) => ({ ...previous, [stage]: !previous[stage] }));

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4">
        {columns.map((column) => {
          const columnItems = byStage(column.value);
          const isFolded = !!folded[column.value];
          const accent = accents[column.value] ?? "bg-muted-foreground/30";

          if (isFolded) {
            return (
              <Droppable key={column.value} droppableId={column.value}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "flex w-14 shrink-0 flex-col items-center gap-2 overflow-hidden rounded-xl border bg-muted/40 pb-3 transition-colors",
                      snapshot.isDraggingOver && "bg-muted ring-2 ring-primary",
                    )}
                  >
                    <div className={cn("h-1 w-full", accent)} />
                    <button
                      type="button"
                      onClick={() => toggle(column.value)}
                      title={`Abrir ${column.label}`}
                      className="flex flex-1 flex-col items-center gap-2 text-muted-foreground hover:text-foreground"
                    >
                      <span className="rounded-full bg-background px-2 text-xs">
                        {columnItems.length}
                      </span>
                      <span className="text-sm font-semibold text-foreground [writing-mode:vertical-rl]">
                        {column.label}
                      </span>
                      <ChevronsLeftRight className="h-3.5 w-3.5" />
                    </button>
                    <div className="hidden">{provided.placeholder}</div>
                  </div>
                )}
              </Droppable>
            );
          }

          return (
            <div
              key={column.value}
              className="flex min-w-64 flex-1 flex-col overflow-hidden rounded-xl border bg-muted/40"
            >
              <div className={cn("h-1", accent)} />
              <div className="flex items-center justify-between gap-2 px-3 py-2.5">
                <h3 className="text-sm font-semibold">{column.label}</h3>
                <div className="flex items-center gap-1">
                  <span className="rounded-full bg-background px-2 text-xs font-medium text-muted-foreground">
                    {columnItems.length}
                  </span>
                  {foldedStages.includes(column.value) ? (
                    <button
                      type="button"
                      onClick={() => toggle(column.value)}
                      title="Plegar columna"
                      aria-label={`Plegar ${column.label}`}
                      className="rounded p-0.5 text-muted-foreground hover:bg-background hover:text-foreground"
                    >
                      <ChevronsLeftRight className="h-3.5 w-3.5" />
                    </button>
                  ) : null}
                </div>
              </div>
              <Droppable droppableId={column.value}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "flex min-h-24 flex-1 flex-col gap-2 px-2 pb-2 transition-colors",
                      snapshot.isDraggingOver && "bg-muted",
                    )}
                  >
                    {columnItems.map((item, index) => (
                      <Draggable
                        key={String(item.id)}
                        draggableId={String(item.id)}
                        index={index}
                      >
                        {(dragProvided, dragSnapshot) => (
                          <div
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            className={
                              dragSnapshot.isDragging
                                ? "rotate-1 opacity-90"
                                : ""
                            }
                          >
                            {renderCard(item)}
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {!columnItems.length ? (
                      <p className="px-1 py-6 text-center text-xs text-muted-foreground">
                        Nada por aquí
                      </p>
                    ) : null}
                    {columnFooter?.(column.value, columnItems)}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
